import { tracking, TrackingData } from "./tracking";
import { GlobalStorage } from "@otto-ec/global-resources";
import { MARK } from "./helper";

/**
 * Custom logic for suggest tracking
 * i.e. create list of suggest terms, track times
 */

export function setInitialSessionStartSended(value: boolean) {
  window.o_util.cookie.set("o_squirrel_sessionStart_sended", value + "");
}

export function isInitialSessionStartSended(): boolean {
  return window.o_util.cookie.get("o_squirrel_sessionStart_sended") == "true";
}

export function getTotalEmptyStateTime(): number {
  performance.measure(MARK.EMPTY_STATE_TIME, MARK.EMPTY_STATE_TIME_START);
  const perfEntries: PerformanceEntryList = performance.getEntriesByName(MARK.EMPTY_STATE_TIME);
  return Math.round(
    perfEntries
      .map((e) => e.duration)
      .reduce((prev: number, current: number): number => {
        return prev + current;
      }, 0)
  );
}

function trackUsage(
  extendByParameters: TrackingData,
  modifiedSearch: boolean,
  trackSuggest: boolean,
  words: any,
  wordLengths: any,
  pos: any,
  originalSearchTerm: string | null
) {
  let storage: GlobalStorage;

  const jsonForTracking: TrackingData = {};
  Object.keys(extendByParameters).forEach((key: string) => {
    jsonForTracking[key] = extendByParameters[key];
  });

  if (modifiedSearch) {
    jsonForTracking["wk.san_SearchType"] = "modified";
  }
  if (trackSuggest) {
    if (!originalSearchTerm) {
      jsonForTracking["wk.san_SearchType"] = "empty_state";
    } else {
      jsonForTracking["wk.san_SearchType"] = "suggest";
    }
    jsonForTracking["wk.san_SuggestSearchTerm"] = originalSearchTerm;
    jsonForTracking.san_SuggestSearchTermLength = originalSearchTerm
      ? originalSearchTerm.length.toString()
      : originalSearchTerm;
    jsonForTracking["wk.san_SuggestPos"] = pos;
    jsonForTracking.san_SuggestList = words;
    jsonForTracking.san_SuggestListWordCount = wordLengths;
  }

  Object.assign(jsonForTracking, getSessionStartLabel());

  if (performance.getEntriesByName(MARK.EMPTY_STATE_TIME_START).length > 0) {
    jsonForTracking["wk.san_EmptyStateTime"] = getTotalEmptyStateTime();
    jsonForTracking["wk.san_EmptyStateCount"] = performance.getEntriesByName(MARK.EMPTY_STATE_TIME_START).length;
  }

  jsonForTracking["wk.san_ActiveSearch"] = "true";
  jsonForTracking.san_InitialSearch = "true";
  jsonForTracking["san_Interaction"] = "initial_search";

  if (tracking.isInitialized()) {
    tracking.sendTrackingDataForNextPI(jsonForTracking);
  } else {
    storage = new window.o_global.Storage(() => window.sessionStorage);
    if (storage.isAvailable) {
      jsonForTracking.san_PageLoadComplete = "false";
      try {
        storage.setItem("squirrel_searchTracking", JSON.stringify(jsonForTracking));
      } catch (e) {
        throw e as Error;
      }
    }
  }
}

function getSessionStartLabel(): TrackingData {
  const data: TrackingData = {};
  const sessionStart = Number(window.o_util.cookie.get("o_squirrel_sessionStart"));
  const currentTime = new Date().getTime();
  const sessionTime = currentTime - sessionStart;
  const ttl = 7200000;
  if (sessionTime > ttl) {
    window.o_util.cookie.set("o_squirrel_sessionStart", performance.timing.navigationStart + "");
    setInitialSessionStartSended(false);
  }

  if (!isInitialSessionStartSended() && performance.getEntriesByName(MARK.INITIAL_SEARCH_BOX_TIME).length > 0) {
    data["san_SquirrelSessionStart"] = sessionStart;
    data["wk.san_InitialSearchBoxTime"] = (
      performance.getEntriesByName(MARK.INITIAL_SEARCH_BOX_TIME).pop() as PerformanceEntry
    ).duration;
    setInitialSessionStartSended(true);
  }
  return data;
}

/**
 * Calculate information about suggestions that is used in tracking later
 */
function writeTrackingSuggestions(searchForm: HTMLFormElement) {
  const suggestions = [].slice
    .call(document.getElementsByClassName("js_squirrel_searchSuggest"))
    .map((st: Node): string => {
      if (st.textContent == undefined) {
        throw new Error("_writeTrackingSuggestions: element without inner text");
      }
      const text: string = st.textContent; // || st.innerText;
      return text.trim();
    });
  const words: string = suggestions.join("|");
  const wordLengths: string = suggestions
    .map((w: string): number => {
      return w.split(" ").length;
    })
    .join("|");

  searchForm.setAttribute("data-suggestWordList", words);
  searchForm.setAttribute("data-suggestWordListCount", wordLengths);
}

/**
 * Sends tracking data
 *
 * @param json encapsulating the data to track
 */
function sendTrackingData(json: TrackingData) {
  tracking.sendTrackingDataForNextPI(json);
}

/**
 * Needed for submit tracking within search-field,js if term was taken by using the arrow
 *
 * @param marker
 * @param searchForm
 */
function markFormForTracking(marker: string, searchForm: HTMLFormElement) {
  searchForm.classList.add(marker);
}

/**
 * Precautionary used by every action excepting a click on an arrow
 *
 * @param marker
 * @param searchForm
 */
function unmarkFormForTracking(marker: string, searchForm: HTMLFormElement) {
  searchForm.classList.remove(marker);
}

/**
 *
 * @return in milliseconds
 */
function trackSuggestTime(): number | null {
  const performanceMeasure: PerformanceMeasure = performance.measure(MARK.SUGGEST_TIME, MARK.SUGGEST_TIME_START);
  return performanceMeasure.duration;
}

export const suggestTracking = {
  trackSuggestTime,
  unmarkFormForTracking,
  markFormForTracking,
  writeTrackingSuggestions,
  trackUsage,
};
